import React from "react";
import { ExecutionStatus } from "@/types/workflow";
import { Badge } from "@/components/ui/badge";
import { 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Loader2, 
  Ban,
  PauseCircle
} from "lucide-react";
import { cn } from "@/lib/utils";

interface ExecutionStatusBadgeProps {
  status: ExecutionStatus;
  size?: "sm" | "md" | "lg";
  showIcon?: boolean;
}

const statusConfig: Record<ExecutionStatus, {
  label: string;
  variant: "default" | "secondary" | "destructive" | "outline";
  icon: React.ComponentType<{ className?: string }>;
  className: string;
}> = {
  pending: {
    label: "Wachtend",
    variant: "secondary",
    icon: Clock,
    className: "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800",
  },
  running: {
    label: "Actief",
    variant: "default",
    icon: Loader2,
    className: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800",
  },
  success: {
    label: "Succesvol",
    variant: "default",
    icon: CheckCircle2,
    className: "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800",
  },
  failed: {
    label: "Mislukt",
    variant: "destructive",
    icon: XCircle,
    className: "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800",
  },
  cancelled: {
    label: "Geannuleerd",
    variant: "outline",
    icon: Ban,
    className: "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800/30 dark:text-gray-400 dark:border-gray-700",
  },
  waiting: {
    label: "Wacht op input",
    variant: "secondary",
    icon: PauseCircle,
    className: "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800",
  },
};

export const ExecutionStatusBadge: React.FC<ExecutionStatusBadgeProps> = ({
  status,
  size = "md",
  showIcon = true,
}) => {
  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  const sizeClasses = {
    sm: "text-xs px-1.5 py-0.5",
    md: "text-sm px-2 py-1",
    lg: "text-base px-3 py-1.5",
  };

  const iconSizes = {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5",
  };

  return (
    <Badge
      variant="outline"
      className={cn(
        "font-medium inline-flex items-center gap-1.5",
        sizeClasses[size],
        config.className
      )}
    >
      {showIcon && (
        <Icon 
          className={cn(
            iconSizes[size],
            status === "running" && "animate-spin"
          )} 
        />
      )}
      {config.label}
    </Badge>
  );
};
